import type { SourceCitationEvidence } from '@onefootprint/request-types/dashboard';
import { NotionEditor } from '@onefootprint/ui';
import type { Editor } from '@tiptap/core';
import type { Ref } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createInlineMarkupRegistry, type InlineToken, linkToBrace, pillEntry } from '@/components/block-primitives';
import { InlineMarkupExtension, inlineMarkupPluginKey } from '@/components/block-primitives/inline-markup-extension';
import type { CitationPair, InvestigationSourceDetail } from '../../../hooks/use-sources';
import useSources, { citationKey } from '../../../hooks/use-sources';
import SourceCitationPopover from '../components/source-citation-popover';
import { ArtifactBlockExtension } from '../extensions/artifact-block-extension';
import { collectDocCitationPairs } from '../inline-markup/collect-doc-citations';
import { buildInlineResolution, InlineResolutionProvider, type InlineResolutionResult } from '../inline-markup/inline-resolution-context';
import { createSourceEntry } from '../inline-markup/kinds/source';
import { parseLocators, sourcesBySequence } from '../source-citation-shared';
type MarkdownViewProps = {
  content: string;
  investigationId?: string;
  // Ref to the inner padded container used by "Download as PDF" to capture the rendered DOM
  contentRef?: Ref<HTMLDivElement>;
  editable?: boolean;
  onChange?: (markdown: string) => void;
  autoFocus?: boolean;
  onAutoFocused?: () => void;
  onCitationPairsChange?: (pairs: CitationPair[]) => void;
  onOpenSources?: () => void;
  onOpenDocument?: (documentIdentifier: string) => void;
  /** Drops the page's scroll area and gutters, for prose laid out inside another view. */
  embedded?: boolean;
  /** The citations to resolve, when several views of one document share a single sources request. */
  sourcePairs?: CitationPair[];
};
type ActiveCitation = {
  rect: DOMRect;
  // The source label shown in the popover header, e.g. `S10`.
  label: string;
  source: InvestigationSourceDetail | undefined;
  // Every resolved passage the chip's run cited — the popover pages through them.
  evidences: SourceCitationEvidence[];
};
const samePairs = (a: CitationPair[], b: CitationPair[]) => a.length === b.length && a.every((pair, i) => pair.sourceLabel === b[i].sourceLabel && pair.locator === b[i].locator);

// Flattens the response's per-source citations into one lookup, so opening a chip's popover reads
// its resolved passages by `(label, locator)` instead of rescanning every source.
const evidenceByCitation = (sources: InvestigationSourceDetail[]) => {
  const byKey = new Map<string, SourceCitationEvidence>();
  for (const source of sources) {
    for (const citation of source.citations) {
      if (citation.evidence) byKey.set(citationKey(source.label, citation.locator), citation.evidence);
    }
  }
  return byKey;
};
const MarkdownView = ({
  content,
  investigationId,
  contentRef,
  editable = false,
  onChange,
  autoFocus = false,
  onAutoFocused,
  onCitationPairsChange,
  onOpenSources,
  onOpenDocument,
  embedded = false,
  sourcePairs
}: MarkdownViewProps) => {
  const editorContentRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeCitation, setActiveCitation] = useState<ActiveCitation | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);

  // Citations parsed from the live editor doc, hydrated together with the sources in one request.
  // The result drives the chip labels and the hover popover, so neither can drift from the prose.
  const [citationPairs, setCitationPairs] = useState<CitationPair[]>([]);
  const {
    data
  } = useSources(investigationId, sourcePairs ?? citationPairs);
  const sources = data?.sources ?? [];
  const sourceLabelsBySequence = sourcesBySequence(sources);
  const evidenceByKey = evidenceByCitation(sources);

  // Feeds block chips their source/state — they render outside the plugin's reach. The React Compiler
  // memoizes this on `data?.sources`, keeping the provider value stable across re-renders.
  const inlineResolution = buildInlineResolution(data?.sources);

  // The prose registry reads resolutions through this getter over a ref, so new sources flow in
  // reactively (the effect below signals a recompute) without remounting.
  const resolveInlineRef = useRef<(token: InlineToken) => InlineResolutionResult>(() => ({
    source: undefined,
    state: 'missing'
  }));
  resolveInlineRef.current = inlineResolution.resolve;
  const normalizedContent = linkToBrace(content);

  // biome-ignore lint/plugin: create-once extension identity; NotionEditor remounts if it changes
  const extensions = useMemo(() => {
    // Prose chips render in detached React roots, so the source kind takes the resolver directly
    // instead of reading the provider below.
    const registry = createInlineMarkupRegistry([createSourceEntry(() => resolveInlineRef.current), pillEntry]);
    return [ArtifactBlockExtension, InlineMarkupExtension.configure({
      registry
    })];
  }, []);
  const onCitationPairsChangeRef = useRef(onCitationPairsChange);
  onCitationPairsChangeRef.current = onCitationPairsChange;

  // Keep the resolve list in sync with the document (re-collect on edits, ignore no-op changes).
  useEffect(() => {
    if (!editor) return;
    const collect = () => {
      const next = collectDocCitationPairs(editor.state.doc);
      setCitationPairs(prev => samePairs(prev, next) ? prev : next);
      onCitationPairsChangeRef.current?.(next);
    };
    collect();
    editor.on('update', collect);
    return () => {
      editor.off('update', collect);
      onCitationPairsChangeRef.current?.([]);
    };
  }, [editor]);

  // Recompute decorations when a sources response lands, so an unresolved chip picks up its label.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta(inlineMarkupPluginKey, true));
  }, [editor, data]);
  const setContentRefs = (node: HTMLDivElement | null) => {
    editorContentRef.current = node;
    if (typeof contentRef === 'function') {
      contentRef(node);
      return;
    }
    if (contentRef) {
      contentRef.current = node;
    }
  };
  const clearCloseTimer = () => {
    if (!closeTimerRef.current) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };
  const closeCitation = () => {
    clearCloseTimer();
    setActiveCitation(null);
  };
  const scheduleCloseCitation = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setActiveCitation(null), 120);
  };
  const openCitation = (chip: HTMLElement) => {
    clearCloseTimer();
    const sequence = Number(chip.dataset.sourceSequence);
    const sourceLabel = `S${chip.dataset.sourceSequence ?? ''}`;
    // A merged chip carries every locator its run cited, and the sources response already resolved
    // them on load — so this is a lookup, never a fetch on hover.
    const evidences = parseLocators(chip.dataset.sourceLocators).map(locator => evidenceByKey.get(citationKey(sourceLabel, locator))).filter((evidence): evidence is SourceCitationEvidence => !!evidence);
    setActiveCitation({
      rect: chip.getBoundingClientRect(),
      label: sourceLabel,
      source: Number.isFinite(sequence) ? sourceLabelsBySequence[sequence] : undefined,
      evidences
    });
  };
  useEffect(() => {
    const root = editorContentRef.current;
    if (!root) return;
    const getCitationChip = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null;
      const chip_0 = target.closest<HTMLElement>('[data-source-chip="true"]');
      return chip_0 && root.contains(chip_0) ? chip_0 : null;
    };

    // Hover is mouse-only: a touch fires pointerout right after the tap, which would close the popover
    // the tap just opened. A tap opens it through the click handler instead.
    const handlePointerOver = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const chip_1 = getCitationChip(event.target);
      if (chip_1) openCitation(chip_1);
    };
    const handlePointerOut = (event_0: PointerEvent) => {
      if (event_0.pointerType !== 'mouse') return;
      const chip_2 = getCitationChip(event_0.target);
      if (!chip_2 || chip_2.contains(event_0.relatedTarget as Node | null)) return;
      scheduleCloseCitation();
    };
    const handleFocusIn = (event_1: FocusEvent) => {
      const chip_3 = getCitationChip(event_1.target);
      if (chip_3) openCitation(chip_3);
    };
    // A press inside the popover also blurs the chip, so focusout closes only on focus landing outside
    // both (a keyboard Tab); presses elsewhere close through the popover's onPointerDownOutside.
    const handleFocusOut = (event_2: FocusEvent) => {
      const chip_4 = getCitationChip(event_2.target);
      const next_0 = event_2.relatedTarget;
      if (!chip_4 || !(next_0 instanceof Element) || chip_4.contains(next_0) || next_0.closest('[data-citation-popover]')) return;
      scheduleCloseCitation();
    };
    const handleClick = (event_3: MouseEvent) => {
      const chip_5 = getCitationChip(event_3.target);
      if (chip_5) {
        event_3.preventDefault();
        openCitation(chip_5);
        return;
      }
      if (!editable || event_3.button !== 0 || event_3.detail > 1 || event_3.shiftKey || !(event_3.target instanceof Element)) return;
      // NotionEditor's `openOnClick: false` leaves editable prose links inert; node views own their links.
      const link = event_3.target.closest<HTMLAnchorElement>('.notion-editor a[href]');
      if (!link || !root.contains(link) || link.closest('[data-node-view-wrapper]')) return;
      if (!link.getAttribute('href') || window.getSelection()?.isCollapsed === false) return;
      event_3.preventDefault();
      window.open(link.href, '_blank', 'noopener,noreferrer');
    };
    root.addEventListener('pointerover', handlePointerOver);
    root.addEventListener('pointerout', handlePointerOut);
    root.addEventListener('focusin', handleFocusIn);
    root.addEventListener('focusout', handleFocusOut);
    root.addEventListener('click', handleClick);
    return () => {
      clearCloseTimer();
      root.removeEventListener('pointerover', handlePointerOver);
      root.removeEventListener('pointerout', handlePointerOut);
      root.removeEventListener('focusin', handleFocusIn);
      root.removeEventListener('focusout', handleFocusOut);
      root.removeEventListener('click', handleClick);
    };
  }, [clearCloseTimer, editable, openCitation, scheduleCloseCitation]);
  const body = <div ref={setContentRefs} className={embedded ? 'artifact-markdown' : 'artifact-markdown py-6 px-4 md:px-16'} aria-busy={embedded ? !editor : undefined}>
      <InlineResolutionProvider value={inlineResolution}>
        <NotionEditor content={normalizedContent} editable={editable} onChange={onChange} extensions={extensions} onEditorReady={editorInstance => {
        setEditor(editorInstance);
        if (!autoFocus) return;
        // Defer so Radix's post-close trigger-refocus (e.g. the `…` actions menu)
        // doesn't steal focus right back. Lands the cursor inside the first node.
        setTimeout(() => {
          editorInstance.commands.focus('start');
          onAutoFocused?.();
        }, 100);
      }} />
      </InlineResolutionProvider>
      <SourceCitationPopover anchorRect={activeCitation?.rect ?? null} label={activeCitation?.label ?? ''} source={activeCitation?.source} evidences={activeCitation?.evidences ?? []} onClose={closeCitation} onPointerEnter={clearCloseTimer} onPointerLeave={scheduleCloseCitation} onOpenSources={onOpenSources} onOpenDocument={onOpenDocument} />
    </div>;
  if (embedded) return body;
  return (
    // The NotionEditor (tiptap) mounts async and fires `onEditorReady`; until then the body is
    // empty and shorter. Mark it busy until the editor is ready so nothing reads the pre-mount
    // height as settled. The mobile bottom padding clears the floating Ask Percy pill; it sits on
    // this scroller rather than the content div so a PDF export doesn't carry it.
    <div className="h-full overflow-auto max-w-(--main-content-max-width) mx-auto w-full pb-14 md:pb-0" aria-busy={!editor}>
      {body}
    </div>
  );
};
export default MarkdownView;